import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { sortCasesNewestFirst } from "./caseFilters";

// Loads a whole case list from `endpoint` (GET, returns an array) and keeps
// it in state. Same return shape as useCases, so the desktop page can take
// either. Optional `pollMs` refetches while the tab is visible. Only an auth
// failure redirects to login; other errors are returned for a retry button.
export default function useCaseList({ endpoint, pollMs, sort = sortCasesNewestFirst }) {
  const [allCases, setAllCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();
  const location = useLocation();
  const controllerRef = useRef(null);

  const fetchList = useCallback(
    async ({ background = false } = {}) => {
      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;
      try {
        const response = await axiosPrivate.get(endpoint, { signal: controller.signal });
        if (controller.signal.aborted) return;
        setAllCases(sort(Array.isArray(response.data) ? response.data : []));
        setError(null);
        setLoading(false);
      } catch (err) {
        if (axios.isCancel(err) || controller.signal.aborted) return;
        console.error(err);
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          navigate("/login", { state: { from: location }, replace: true });
          return;
        }
        // A failed background refresh keeps showing the last list.
        if (!background) setError(err);
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [axiosPrivate, endpoint, sort]
  );

  useEffect(() => {
    setLoading(true);
    fetchList();
    return () => controllerRef.current?.abort();
  }, [fetchList]);

  useEffect(() => {
    if (!pollMs) return undefined;
    const timer = window.setInterval(() => {
      if (!document.hidden) fetchList({ background: true });
    }, pollMs);
    return () => window.clearInterval(timer);
  }, [pollMs, fetchList]);

  const addCase = useCallback((singleCase) => {
    setAllCases((prev) => [singleCase, ...prev]);
  }, []);

  const removeCase = useCallback((caseId) => {
    const target = String(caseId);
    setAllCases((prev) => prev.filter((c) => String(c.id) !== target));
  }, []);

  const updateCase = useCallback((caseId, patch) => {
    const target = String(caseId);
    setAllCases((prev) =>
      prev.map((c) =>
        String(c.id) === target
          ? { ...c, ...(typeof patch === "function" ? patch(c) : patch) }
          : c
      )
    );
  }, []);

  const refresh = useCallback(() => fetchList({ background: true }), [fetchList]);

  return { allCases, loading, error, addCase, removeCase, updateCase, refresh };
}
